import {
	AfterViewInit,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	DestroyRef,
	EventEmitter,
	inject,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	Signal,
	signal,
	SimpleChanges,
	ViewChild,
	ViewContainerRef,
} from '@angular/core';
import {
	IAuFileMetadata,
	IDroppedFile,
	IFileTypeConfig,
	IFileUploadEvent,
	IUploadOptions,
} from '@shared/ui-components/au-file-loader/au-file-loader.interfaces';
import { CmmsPages } from '@app/pages/cmms/state/cmms.reducer';
import { TableType } from '@app/pages/cmms/types';
import {
	FileLoaderService,
	IFileLoaderOptions,
} from '@shared/ui-components/au-file-loader/services/file-loader.service';
import { FileUtilities } from '@shared/ui-components/au-file-loader/helpers/file-utilities';
import { AuUtilsFunctions } from '@app/shared';
import { FileListTools } from '@shared/ui-components/au-file-loader/helpers/file-list-helpers';
import { AuDialogService } from '@core/services/au-dialog.service';
import { AuErrorService } from '@core/services/au-error-service';
import { FileSelectionsService } from '@shared/ui-components/au-file-loader/services/file-selections.service';
import { FileValidation } from '@shared/ui-components/au-file-loader/helpers/file-validation.class';
import { FormControl } from '@angular/forms';
import { Observable } from 'rxjs';
import { fileTypesConfig } from '@shared/ui-components/au-file-loader/au-file-loader.config';
import { filter, tap } from 'rxjs/operators';
import { CmmsFiltersService } from '@app/pages/cmms/components/cmms-sort-filters-grouping/services/cmms-filters.service';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
	IAuPaginationData,
	IDjangoPaginationData,
} from '@shared/ui-components/au-paginator/pagination-page.info';
import { IconSnackBarService } from '@app/core/services/icon-snack-bar.service';
import { AuExpansionPanelModule } from '@app/shared/ui-components/au-expansion-panel/au-expansion-panel.module';
import { OngoingCounterComponent } from '@app/shared/ui-components/au-file-loader/au-file-load-floating-window/ongoing-counter/ongoing-counter.component';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { CmmsAppliedFiltersBarComponent } from '../../cmms-sort-filters-grouping/cmms-applied-filters-bar/cmms-applied-filters-bar.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AuPaginatorModule } from '@app/shared/ui-components';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { ICmmsSelectedFilter } from '../../cmms-sort-filters-grouping/interfaces-and-types/filter.interfaces';
import { AppliedFilterButtonComponent } from '../../cmms-sort-filters-grouping/cmms-applied-filters-bar/applied-filter-button/applied-filter-button.component';
import { TranslateService } from '@tolgee/ngx';
import { NgxTolgeeModule } from '@tolgee/ngx';
import { FileCardsListComponent } from '@app/shared/ui-components/au-file-loader/file-cards-list/file-cards-list.component';
import { FilesToolbarComponent } from '@app/shared/ui-components/au-file-loader/files-toolbar/files-toolbar.component';
@Component({
	selector: 'file-section',
	templateUrl: './file-section.component.html',
	styleUrls: ['./file-section.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		AuExpansionPanelModule,
		OngoingCounterComponent,
		MatIconModule,
		MatMenuModule,
		CmmsAppliedFiltersBarComponent,
		MatTooltipModule,
		AuPaginatorModule,
		MatProgressSpinnerModule,
		AppliedFilterButtonComponent,
		NgxTolgeeModule,
		FileCardsListComponent,
		FilesToolbarComponent,
	],
})
export class FileSectionComponent
	extends FileListTools
	implements OnInit, AfterViewInit, OnChanges, OnDestroy
{
	@Input() askDeleting = true;
	@Input() options: Partial<IFileLoaderOptions>;
	@Input() loadedFiles: Partial<IDroppedFile>[];
	@Input() relatedFiles: IAuFileMetadata[];
	@Input() totalRelatesFiles: number;
	@Input() showRelatedFiles: boolean;
	@Input() disableBulkSelections = false;
	@Input() enableFileListScroll = false;
	@Input() hideCounter = false;
	@Input() restrictFileSourceMenu = false;
	@Input() showFileTypesHint = false;
	@Input() fullFileLoadData = false;
	@Input() uploadTitle: string;
	@Input() delegateDeleting = false;
	@Input() paginationData: IDjangoPaginationData;

	@Output() filesLoaded = new EventEmitter<string[]>();
	@Output() fullFileLoaded = new EventEmitter<Partial<IDroppedFile>[]>();
	@Output() filesDeleted = new EventEmitter<{
		ids: string[];
		onSuccess: () => void;
	}>();
	@Output() controlReady = new EventEmitter<FormControl>();
	@Output() fileListChanged = new EventEmitter<Partial<IDroppedFile>[]>();
	@Output() addExistingFileClicked = new EventEmitter<{
		page: CmmsPages;
		tableType: TableType;
	}>();
	@Output() paginationChanged = new EventEmitter<IAuPaginationData>();
	@Output() filterChanged = new EventEmitter();
	@Output() rowSelect = new EventEmitter<any>();

	private destroyRef = inject(DestroyRef);
	formControl = new FormControl([], []);
	fileCouldBeDropped: boolean;
	filesList = signal<Partial<IDroppedFile>[]>([]);
	filteredFileList: Partial<IDroppedFile>[] = [];
	relatedFilesList: Partial<IDroppedFile>[] = [];
	niceFormattedFileTypes: string;
	fileValidation: FileValidation;
	fileTypesConfig: IFileTypeConfig = fileTypesConfig;
	selection = this.selectionsService.selections;
	selections$: Observable<string[]>;
	relatedFileSelected: boolean = false;
	loadedFilesSelected: boolean = false;
	filePage = CmmsPages.files;
	relatedFilesLoading = signal(false);
	pageSize = signal(10);

	selectedFilters: Signal<ICmmsSelectedFilter[]> = toSignal(
		this.filterService.selectedFilters$
	);

	@ViewChild('fileInput', { read: ViewContainerRef })
	private fileInputRef: ViewContainerRef;

	private loadersCounter = 0;
	private filtrationValues = this.getInitalFIlterValue();

	constructor(
		private fileLoader: FileLoaderService,
		private auDialogService: AuDialogService,
		private auErrorService: AuErrorService,
		private cdr: ChangeDetectorRef,
		private selectionsService: FileSelectionsService,
		private filterService: CmmsFiltersService,
		private notify: IconSnackBarService,
		private translateService: TranslateService
	) {
		super();
	}

	ngOnInit() {
		this.setFileFilters();
		this.fileTypesConfig = this.options?.fileTypesConfig || fileTypesConfig;
		this.fileValidation = new FileValidation(this.fileTypesConfig);

		this.controlReady.emit(this.formControl);
		this.selections$ = this.selectionsService.selections$;

		this.niceFormattedFileTypes = this.fileValidation.getNiceFormattedFileTypes();

		this.trackSelectedFiles();

		this.subscribeToFilters();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.loadedFiles) {
			this.pushNewFilesFromChangesToFileList(changes.loadedFiles.currentValue);
			this.filteredFileList = [...this.filesList()];
			this.filtrationValues = this.getInitalFIlterValue();
		}

		if (changes.relatedFiles) {
			this.relatedFilesList = changes.relatedFiles.currentValue
				.map(FileUtilities.makeObjectForExistingFile)
				.sort(FileUtilities.sortFilesByDate);
			this.relatedFilesLoading.set(false);
		}

		if (changes.options?.currentValue?.fileTypesConfig) {
			this.fileTypesConfig = changes.options.currentValue.fileTypesConfig;
			this.fileValidation = new FileValidation(this.fileTypesConfig);
		}
	}

	ngAfterViewInit() {
		if (this.options?.instantlyOpenSelectionDialog) {
			this.fileInputRef.element.nativeElement.click();
		}
	}

	trackSelectedFiles() {
		this.selection.changed.subscribe(selectionChange => {
			const addedFile = selectionChange.added[0];
			if (selectionChange.source.selected.length === 0) {
				this.updateSelectionFlags({ related: false, attached: false });
				return;
			} else if (!addedFile) {
				return;
			}
			this.checkSelectedType(addedFile);
		});
	}
	checkSelectedType(addedFile: Partial<IDroppedFile>) {
		let relatedFileChecked: boolean = false;
		if (this.relatedFiles.some(file => file.pk === addedFile?.pk)) {
			relatedFileChecked = true;
		}
		if (
			(this.relatedFileSelected && !relatedFileChecked) ||
			(this.loadedFilesSelected && relatedFileChecked)
		) {
			this.selectionsService.selectionClear$.next();
			this.selection.select(addedFile);
		}
		this.updateSelectionFlags({
			related: relatedFileChecked,
			attached: !relatedFileChecked,
		});
	}

	updateSelectionFlags(relatedOrAttached: { related: boolean; attached: boolean }) {
		this.relatedFileSelected = relatedOrAttached.related;
		this.loadedFilesSelected = relatedOrAttached.attached;
	}

	onFileChanged(event) {
		const files = FileUtilities.getFilesFromEvent(event);

		for (let i = 0; i < files.length; i++) {
			this.startLoadProcedure(files[i]);
		}
	}

	openFile(index: number, openRelated = false) {
		if (openRelated) {
			return this.rowSelect.emit(this.relatedFiles[index]);
		}
		if (this.loadedFiles[index].createdBy) {
			return this.rowSelect.emit(
				this.loadedFiles.find(file => file.pk === this.filteredFileList[index].pk)
			);
		}
		this.notify.error(this.translateService.instant('files_section_wait_file_loaded'));
	}

	addExistingHandler(event: Event) {
		event.stopPropagation();
		this.addExistingFileClicked.emit({
			page: CmmsPages.existingEntities,
			tableType: TableType.FILES,
		});
	}

	addButtonClickHandler(event: Event, fileInput: HTMLElement) {
		event.preventDefault();
		fileInput.click();
	}

	onDragFileEnter(event: DragEvent) {
		event.preventDefault();
		event.stopPropagation();
		this.fileCouldBeDropped = true;
	}

	onDragFileLeave(event: DragEvent) {
		event.preventDefault();
		event.stopPropagation();
		this.fileCouldBeDropped = false;
	}

	onDragOver(event: DragEvent) {
		event.preventDefault();
		event.stopPropagation();
		event.dataTransfer.dropEffect = 'link';
		// this.fileCouldBeDropped = true;
	}

	handleImageDrop(event: DragEvent) {
		event.preventDefault();
		this.fileCouldBeDropped = false;

		this.onFileChanged(event.dataTransfer);
	}

	deleteItemFromFileList(index: number) {
		if (this.filesList[index].newlyLoaded) {
			this.removeFileByIndex(index);
		} else {
			// We need to ensure user that he deleted file loaded by someone else.
			// Because it's impossible to restore such a file
			if (this.askDeleting) {
				this.askUserAndRemoveFile(index);
			} else {
				this.removeFileByIndex(index);
			}
		}
	}

	clearSelectionPressed() {
		this.selectionsService.selectionClear$.next();
	}

	paginationUpdateHandler(pagination: IAuPaginationData) {
		this.paginationChanged.emit(pagination);
		this.relatedFilesLoading.set(true);
		this.pageSize.set(pagination.first);
	}

	async deleteMultipleItemsFromFileList() {
		const filePk = this.selection.selected.map(file => file.pk);

		if (this.hasOldAndMarkedForDeletionFile(filePk)) {
			this.askUserAndRemoveMultipleFiles(filePk);
		} else {
			this.removeMultipleFiles(filePk);
		}
	}

	private askUserAndRemoveMultipleFiles(selections): void {
		this.askUserAboutDeletion(selections.length).subscribe((confirm: boolean) => {
			if (confirm) {
				this.removeMultipleFiles(selections);
			}
		});
	}

	private askUserAboutDeletion(length: number): Observable<boolean> {
		const dialogRef = this.auDialogService.openDeleteDialog({
			title: this.translateService.instant('dialog_delete_file_title'),
			description: this.translateService.instant('dialog_delete_files_description', {
				count: length,
			}),
		});

		return this.auDialogService.closedByEnter(dialogRef);
	}

	private removeMultipleFiles(selections: string[]) {
		if (this.delegateDeleting) {
			this.emitDeletion(selections);
		} else {
			this.showDeleteLoading(selections);
			const removeSubscription = this.fileLoader.remove(selections);

			removeSubscription.subscribe({
				next: () => {
					this.handleSuccessfulRemoval(selections);
				},
				error: error => {
					this.handleFailedRemoval(selections, error);
				},
			});
			removeSubscription.subscribe({
				next: () => {
					this.handleSuccessfulRemoval(selections);
				},
				error: error => {
					this.handleFailedRemoval(selections, error);
				},
			});
		}
	}

	private handleSuccessfulRemoval(selections: string[]) {
		if (this.delegateDeleting) {
			this.emitDeletion(selections);
		} else {
			this.filesList.set(this.filesList().filter(file => !selections.includes(file.pk)));
			this.emitDeletion(selections);
			this.clearAllDeletionLoaders(selections);
		}
	}

	private handleFailedRemoval(selections: string[], error: any) {
		this.auErrorService.notifyUserAboutError({
			error,
			defaultMessage: this.translateService.instant('files_section_cant_delete_file'),
		});
		this.clearAllDeletionLoaders(selections);
	}

	private startLoadProcedure(file: File): void {
		const errors = this.fileValidation.getFileValidationErrors(file);
		const fileObject: Partial<IDroppedFile> = FileUtilities.makeObjectForNewFile(
			file,
			errors
		);
		this.filesList.update(currentFiles => [...currentFiles, fileObject]);
		const lastIndex = this.lastFileIndex;

		if (AuUtilsFunctions.objectNotEmpty(errors)) {
			this.fileCouldBeDropped = false;
			fileObject.requestBrakeSubject.complete();

			return;
		}

		this.filesList()[lastIndex].file = file;
		this.startHttpLoading(file);
	}

	private startHttpLoading(file: File, index = this.lastFileIndex) {
		const requestBrakeSubject$ = this.filesList()[index].requestBrakeSubject;
		const uploadOptions: IUploadOptions = {
			file,
			index,
			requestBrakeSubject$: requestBrakeSubject$,
			mutation: this.options?.mutation,
			mutationVariables: this.options?.mutationVariables,
			endpointName: this.options?.endpointName,
		};

		this.loadersCounter++;
		this.fileLoader
			.upload(uploadOptions)
			.pipe(
				tap((data: IFileUploadEvent) => this.handleOngoingEvent(data)),
				filter((data: IFileUploadEvent) => FileUtilities.finishEvent(data))
			)
			.subscribe({
				next: response => this.handleFinishEvent(response),
				error: error => {
					this.loadersCounter--;
					console.error(error);
				},
			});
	}

	private handleOngoingEvent(data: IFileUploadEvent): void {
		if ('error' in data.event && data.event['error']) {
			this.handleHttpError(data.event['error']);
		}

		if (FileUtilities.progressEvent(data) && !!this.filesList[data.index]) {
			this.handleProgressEvent(data);
		}

		this.cdr.detectChanges();
	}

	private handleFinishEvent(response): void {
		this.loadersCounter--;
		this.updateFileInFilesList(response);

		if (this.loadersCounter === 0 && this.onlyNewlyDownloadedFiles.length) {
			if (this.fullFileLoadData) {
				this.fullFileLoaded.emit(this.filesList());
			} else {
				this.filesLoaded.emit(this.onlyNewlyDownloadedFiles);
			}

			this.formControl.setValue(this.onlyNewlyDownloadedFiles);
			this.fileListChanged.emit(this.filesList());
			this.filesList.set(
				this.filesList().map(file => ({ ...file, notEmittedYet: false }))
			);
		}
	}

	private emitDeletion(ids: string[]) {
		this.filesDeleted.emit({
			ids,
			onSuccess: () => {
				this.selectionsService.removeSelections(ids);
			},
		});
	}

	private deleteFile(index: number): void {
		const pk = this.filesList[index].pk;

		this.fileLoader.remove([pk]).subscribe({
			next: () => {
				this.hideDeletionLoader(index);
				this.emitDeletion([pk]);
				this.removeItemFromFileList(index);
			},
			error: error => {
				this.hideDeletionLoader(index);
				this.auErrorService.notifyUserAboutError({
					error,
					defaultMessage: "Can't delete file. Something went wrong",
				});
			},
		});
	}

	private removeFileByIndex(index: number): void {
		if (this.delegateDeleting) {
			this.emitDeletion([this.filesList[index].pk]);
		} else {
			if (!this.filesList[index].pk) {
				this.removeItemFromFileList(index);
				return;
			}

			this.showDeletionLoader(index);
			this.deleteFile(index);
		}
	}

	private askUserAndRemoveFile(index): void {
		const dialogRef = this.auDialogService.openDeleteDialog({
			title: this.translateService.instant('dialog_delete_file_title'),
			description: this.translateService.instant('dialog_delete_file_description', {
				name: this.filesList[index].name,
			}),
		});

		this.auDialogService.closedByEnter(dialogRef).subscribe((confirm: boolean) => {
			if (confirm) {
				this.removeFileByIndex(index);
			}
		});
	}

	private setFileFilters() {
		this.filterService.setAvailableFilters([
			{
				name: this.translateService.instant('files_section_document_type'),
				type: 'documentType',
				columnName: 'type',
			},
			{
				name: this.translateService.instant('files_section_classification'),
				type: 'classification',
				columnName: 'classifications',
			},
		]);
	}

	private subscribeToFilters(): void {
		this.filterService.selectedFiltersVariables$
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(filterVariables => {
				this.filtrationValues = {
					...this.getInitalFIlterValue(),
					...filterVariables,
				};
				this.relatedFilesLoading.set(true);
				this.filterFiles();
			});

		this.filterService.quickSearchValue$
			.pipe(
				takeUntilDestroyed(this.destroyRef),
				filter(searchText => searchText !== null)
			)
			.subscribe(search => {
				this.filtrationValues = { ...this.filtrationValues, search };
				this.relatedFilesLoading.set(true);
				this.filterFiles();
			});
	}

	private filterFiles() {
		let files = [...this.filesList()];

		if (this.filtrationValues.type.length) {
			const filterTypeCallback = (file: IDroppedFile) =>
				file.type ? this.filtrationValues.type.includes(file.type) : false;

			files = files.filter(filterTypeCallback);
		}

		if (this.filtrationValues.classifications?.length) {
			const filterClassificationCallback = (file: IDroppedFile) => {
				let include = false;
				for (let i = 0; i < this.filtrationValues.classifications.length; i++) {
					if (
						file.classificationsV2.find(
							classification =>
								classification.pk === this.filtrationValues.classifications[i]
						)
					) {
						include = true;
						break;
					}
				}

				return include;
			};

			files = files.filter(filterClassificationCallback);
		}

		if (this.filtrationValues.search?.length) {
			const loverCaseSearch = this.filtrationValues.search.toLowerCase();
			const fileSearchCallback = file => {
				return (
					(file.type &&
						this.getFileTypeName(file.type).toLowerCase().includes(loverCaseSearch)) ||
					file.name.toLowerCase().includes(loverCaseSearch)
				);
			};

			files = files.filter(fileSearchCallback);
		}

		this.filteredFileList = files;
	}

	private getInitalFIlterValue() {
		return {
			search: '',
			type: [],
			classifications: [],
		};
	}

	notifyAssetForLink() {
		const files: Partial<IDroppedFile>[] = this.selection.selected;
		this.filesList.update(currentFiles => [
			...currentFiles,
			...files.map(file => ({ ...file, url: file?.url })),
		]);
		this.fileListChanged.emit(this.filesList());
		this.selectionsService.selections.clear();
	}

	ngOnDestroy() {
		this.selectionsService.selections.clear();
	}
}
